/**
 * @file Shared enums for the calendar library.
 * These enums are safe to use in both client and server environments.
 */

/**
 * Event types for musician-specific functionality.
 */
export const EVENT_TYPES = {
  GIG: 'gig',
  LESSON: 'lesson',
  AUDITION: 'audition',
  PRACTICE: 'practice',
  REHEARSAL: 'rehearsal',
  RECORDING: 'recording',
  MEETING: 'meeting'
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

/**
 * Payment status options.
 */
export const PAYMENT_STATUS = {
  PENDING: 'Pending',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled'
} as const;

export type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

/**
 * Event status options.
 */
export const EVENT_STATUS = {
  CONFIRMED: 'Confirmed',
  TENTATIVE: 'Tentative',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed'
} as const;

export type EventStatus = typeof EVENT_STATUS[keyof typeof EVENT_STATUS];

/**
 * Calendar types.
 */
export const CALENDAR_TYPES = {
  INDIVIDUAL: 'individual',
  GROUP: 'group',
  SHARED: 'shared'
} as const;

export type CalendarType = typeof CALENDAR_TYPES[keyof typeof CALENDAR_TYPES];

/**
 * Student levels for lessons.
 */
export const STUDENT_LEVELS = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
  PROFESSIONAL: 'Professional'
} as const;

export type StudentLevel = typeof STUDENT_LEVELS[keyof typeof STUDENT_LEVELS];

/**
 * Difficulty levels for pieces/repertoire.
 */
export const DIFFICULTY_LEVELS = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
  EXPERT: 'Expert'
} as const;

export type DifficultyLevel = typeof DIFFICULTY_LEVELS[keyof typeof DIFFICULTY_LEVELS];

/**
 * Common musical genres.
 */
export const GENRES = {
  CLASSICAL: 'Classical',
  JAZZ: 'Jazz',
  ROCK: 'Rock',
  POP: 'Pop',
  BLUES: 'Blues',
  COUNTRY: 'Country',
  FOLK: 'Folk',
  ELECTRONIC: 'Electronic',
  WORLD: 'World Music',
  OTHER: 'Other'
} as const;

export type Genre = typeof GENRES[keyof typeof GENRES];

/**
 * Common instruments.
 */
export const INSTRUMENTS = {
  PIANO: 'Piano',
  GUITAR: 'Guitar',
  VIOLIN: 'Violin',
  DRUMS: 'Drums',
  BASS: 'Bass',
  SAXOPHONE: 'Saxophone',
  TRUMPET: 'Trumpet',
  FLUTE: 'Flute',
  CELLO: 'Cello',
  VOICE: 'Voice',
  OTHER: 'Other'
} as const;

export type Instrument = typeof INSTRUMENTS[keyof typeof INSTRUMENTS]; 